import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useTicketPerGameSlot } from "pages/ticket/useTickets";
import TableTwoContainer from "Common/TableTwoContainer";

const TicketsPerGameSlot = () => {
  const { gameSlotId } = useParams<{ gameSlotId: string }>();

  const [filterType, setFilterType] = useState("ticketId");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPlaceholder, setSearchPlaceholder] = useState(
    "Enter Ticket Number"
  );

  const size = 20;

  const {
    tickets,
    isFetching,
    hasNextPage,
    isLoading,
    hasPreviousPage,
    goToNextPage,
    goToPreviousPage,
    totalPages,
    currentPage,
    refetchTickets,
  } = useTicketPerGameSlot(
    gameSlotId,
    size,
    filterType === "ticketId" ? searchQuery : "",
    filterType === "phoneNumber" ? searchQuery : "",
    filterType === "createdDate" && searchQuery ? `${searchQuery}T00:00:00` : ""
  );

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedFilter = e.target.value;
    setFilterType(selectedFilter);
    setSearchQuery("");

    switch (selectedFilter) {
      case "ticketId":
        setSearchPlaceholder("Enter Ticket Number");
        break;
      case "phoneNumber":
        setSearchPlaceholder("Enter Phone Number");
        break;
      case "createdDate":
        setSearchPlaceholder("Select Created Date");
        break;
      default:
        setSearchPlaceholder("Search...");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    refetchTickets();
  };

  const columns = useMemo(
    () => [
      {
        header: "Ticket Number",
        accessorKey: "ticketNumber",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="transition-all duration-150 ease-linear order_id text-custom-500">
            {cell.getValue()}
          </span>
        ),
      },

      {
        header: "Ticket Type",
        accessorKey: "ticketType",
        enableColumnFilter: false,
        cell: (cell: any) => cell.getValue() || "N/A",
      },
      {
        header: "Amount",
        accessorKey: "ticketAmount",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span className="text-sm text-slate-500 dark:text-zink-200 ">
            {" ₦  " + cell.getValue()}
          </span>
        ),
      },

      {
        header: "User Phone Number",
        accessorKey: "phoneNumber",
        enableColumnFilter: false,
      },
      {
        header: "Ticket Status",
        accessorKey: "ticketStatus",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <span
            className={`text-sm font-semibold py-1 px-2.5 rounded-full text-white ${
              cell.getValue() === "LOST"
                ? "bg-red-500"
                : cell.getValue() === "WON"
                ? "bg-green-500"
                : cell.getValue() === "NEW"
                ? "bg-yellow-500"
                : cell.getValue() === "USED"
                ? "bg-blue-500"
                : ""
            }`}
          >
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "Created Date",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const dateArray = cell.getValue();
          const date = new Date(
            dateArray[0], // year
            dateArray[1] - 1, // month (0-based index)
            dateArray[2], // day
            dateArray[3], // hour
            dateArray[4], // minute
            dateArray[5], // second
            dateArray[6] / 1000000 // milliseconds
          );

          return (
            <span className="text-sm text-slate-500 dark:text-zink-200">
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />
      <div className="card" id="ordersTable">
        <div className="card-body  relative overflow-x-scroll">
          <div className="relative flex gap-3 items-center">
            {/* Filter Dropdown */}

            <select
              className="border border-slate-200 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800   dark:border-zink-500  dark:text-zink-100  rounded-lg  px-3 py-2.5 dark:bg-zink-700  "
              onChange={handleFilterChange}
            >
              <option value="ticketId"> Ticket Number</option>
              <option value="phoneNumber">Phone Number</option>
              <option value="createdDate">Created Date</option>
            </select>

            {/* Dynamic Search Input */}
            {filterType === "createdDate" ? (
              <input
                type="date"
                value={searchQuery}
                onChange={handleSearchChange}
                className="border border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 rounded px-3 py-2 dark:bg-zink-700 dark:text-white"
              />
            ) : (
              <div className="grid grid-cols-1 gap-4 ">
                <div className="col-span-3">
                  <div className="relative">
                    <input
                      value={searchQuery}
                      onChange={handleSearchChange}
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder={searchPlaceholder}
                      autoComplete="off"
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>
              
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="noresult">Loading...</div>
          ) : tickets.length > 0 ? (
            <div className="mt-10">
              <TableTwoContainer columns={columns || []} data={tickets || []} />

              <div className="mt-4 flex justify-end items-center gap-4">
                {/* Previous Page Button */}
                <button
                  onClick={goToPreviousPage}
                  disabled={!hasPreviousPage}
                  className="flex items-center px-3 py-1 bg-gray-500 text-white rounded disabled:opacity-50"
                >
                  <FaChevronLeft className="mr-2" />
                  {isFetching ? "Loading..." : "Prev"}
                </button>

                {/* Page Indicator */}
                <span className="text-base font-medium">
                  Page {currentPage + 1} of {totalPages}
                </span>

                {/* Next Page Button */}
                <button
                  onClick={goToNextPage}
                  disabled={!hasNextPage || isFetching}
                  className="flex items-center px-3 py-1 bg-blue-500 text-white rounded disabled:opacity-50"
                >
                  {isFetching ? "Loading..." : "Next"}
                  <FaChevronRight className="ml-2" />
                </button>
              </div>
            </div>
          ) : (
            <div className="noresult my-5">
              <h6>No tickets found</h6>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default TicketsPerGameSlot;
